import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { DebitPermissionDto } from "../../../../../api-client";
import Controls from "../../../../../atoms/Controls";
import { v4 as uuidv4 } from "uuid";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import { useCreateOrUpdateCreditPermission } from "../../../../../stores/Transactions/transaction.mutations";
import {
  useGetUniquePairsForPatient,
  useDuePaymentByRxId,
} from "../../../../../stores/Transactions/transaction.queries";
import { observer } from "mobx-react";
import RefundTransactionForm from "./RefundTransactionForm";

interface CreditPermissionProps {
  open: boolean;
  handleTransactionClose: () => void;
  patientData: any;
  rxId?: string; // Rx ID for fetching due payment
  selectedRow?: any; // Optional - only used when editing existing permission
  isSingleUse: boolean;
  isEditing: boolean;
  userId?: string;
  locationId?: string;
  onPermissionCreated?: (permission: DebitPermissionDto) => void;
}

/**
 * CreditPermission component for creating/editing SEPA credit permissions (for refunds)
 * Similar to DebitPermission but used for sending money TO patients (refunds)
 */
const CreditPermission: React.FC<CreditPermissionProps> = observer(
  ({
    open,
    handleTransactionClose,
    patientData,
    rxId,
    selectedRow,
    isSingleUse,
    isEditing,
    userId,
    locationId,
    onPermissionCreated,
  }) => {
    const { mutate: createOrUpdateCreditPermission } =
      useCreateOrUpdateCreditPermission();
    const { data: uniquePairsData } = useGetUniquePairsForPatient(
      patientData?.id,
    );
    const { data: duePaymentData } = useDuePaymentByRxId(
      rxId || selectedRow?.id, // Use rxId prop first, fall back to selectedRow.id for editing
      patientData?.id,
      isSingleUse,
    );
    const [selectedAccountId, setSelectedAccountId] = useState<
      string | undefined
    >(undefined);
    const [showRefundForm, setShowRefundForm] = useState(false);
    const [createdPermission, setCreatedPermission] = useState<
      DebitPermissionDto | undefined
    >(undefined);

    const accounts = useUserAccounts();
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const initialFormValues: DebitPermissionDto = {
      id: uuidv4(),
      firstName: patientData?.firstName || "",
      lastName: patientData?.lastName || "",
      iban: "",
      bic: "",
      amount: null,
      patient: patientData,
      isSingleUse: isSingleUse,
    };

    const [formValues, setFormValues] =
      useState<DebitPermissionDto>(initialFormValues);

    const validateForm = () => {
      const requiredFields: Array<keyof DebitPermissionDto> = [
        "firstName",
        "lastName",
        "id",
        "account",
      ];

      const isValid = requiredFields.every((field) => {
        const value: any = formValues[field];
        return value && value !== "";
      });

      setIsFormValid(isValid);
      return isValid;
    };

    useEffect(() => {
      const fetchData = async () => {
        let updatedValues = { ...initialFormValues };

        if (isEditing && selectedRow) {
          // Editing existing permission - use all data from selectedRow
          updatedValues = selectedRow;
          setSelectedAccountId(selectedRow?.account?.id);
        } else if (isSingleUse && duePaymentData) {
          // Creating new single-use permission from refund flow
          // Only prefill the amount, user selects account from dropdown
          updatedValues = {
            ...updatedValues,
            amount: Math.abs(Number(duePaymentData?.dueAmount)) || null,
            isSingleUse: true,
          };
        } else if (isSingleUse && selectedRow) {
          // Creating from payment flow (has selectedRow with account)
          updatedValues = {
            ...updatedValues,
            account: selectedRow.account || "",
            amount: Math.abs(Number(duePaymentData?.dueAmount)) || null,
            isSingleUse: true,
          };
          setSelectedAccountId(selectedRow?.account?.id);
        }

        setFormValues(updatedValues);
      };

      fetchData();
    }, [isSingleUse, selectedRow, duePaymentData, isEditing]);

    useEffect(() => {
      validateForm();
    }, [formValues]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (name === "account") {
        setFormValues((prevValues: any) => ({
          ...prevValues,
          account: accounts?.find((option) => option.id === value),
        }));
        setSelectedAccountId(value);
      } else if (name === "amount") {
        setFormValues((prevValues: any) => ({
          ...prevValues,
          amount: value || null,
        }));
      } else {
        setFormValues((prevValues) => ({
          ...prevValues,
          [name]: value,
        }));
      }
    };

    const handlePairSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedValue = event.target.value;
      const selectedPair = uniquePairsData?.find(
        (pair) =>
          `${pair.firstName} ${pair.lastName} - IBAN: ${pair.iban}` ===
          selectedValue,
      );

      if (selectedPair) {
        setFormValues((prevValues) => ({
          ...prevValues,
          firstName: selectedPair.firstName,
          lastName: selectedPair.lastName,
          iban: selectedPair.iban,
          bic: selectedPair.bic || "",
        }));
      }
    };

    const handleSubmit = async () => {
      try {
        if (!validateForm()) {
          notificationStore.showMessage(
            "Bitte füllen Sie die erforderlichen Felder aus",
            "warning",
          );
          return;
        }
        setLoading(true);

        createOrUpdateCreditPermission(
          {
            patientId: patientData?.id,
            dto: {
              ...formValues,
              amount: formValues.amount ? Number(formValues.amount) : null,
            },
          },
          {
            onSuccess: (response: any) => {
              if (onPermissionCreated && response) {
                onPermissionCreated(response);
              }
              // If single-use from refund flow and we have userId/locationId, show refund form
              if (
                isSingleUse &&
                !isEditing &&
                userId &&
                locationId &&
                selectedRow
              ) {
                setCreatedPermission(response);
                setShowRefundForm(true);
              } else {
                handleTransactionClose();
              }
            },
            onError: (error: any) => {
              console.error("Error creating credit permission:", error);
            },
            onSettled: () => {
              setLoading(false);
            },
          },
        );
      } catch (error) {
        console.error("Error submitting credit permission:", error);
        setLoading(false);
      }
    };

    return (
      <>
        {showRefundForm &&
        createdPermission &&
        (rxId || selectedRow?.id) &&
        userId &&
        locationId ? (
          <RefundTransactionForm
            open={true}
            handleTransactionClose={handleTransactionClose}
            patientId={patientData?.id || 0}
            rxId={rxId || selectedRow?.id || ""}
            userId={userId}
            locationId={locationId}
            creditPermission={createdPermission}
            handleReturnToPatient={async () => {}} // Not used for credit transfer
          />
        ) : (
          <Dialog
            open={open}
            onClose={handleTransactionClose}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>
              SEPA Gutschriftmandat (Rückerstattung) -{" "}
              {isSingleUse ? "Einmalig" : "Mehrfach"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Left Side: Form Fields */}
                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    {/* Mandate Reference */}
                    <Grid item xs={12}>
                      <Controls.Input
                        label="Mandatsreferenz"
                        name="id"
                        value={formValues.id}
                        onChange={handleChange}
                        fullWidth
                        disabled
                        required
                      />
                    </Grid>

                    {/* Account Select */}
                    <Grid item xs={12}>
                      <Controls.Select
                        name="account"
                        label="Mandant"
                        value={selectedAccountId || ""}
                        onChange={handleChange}
                        options={accounts?.map((option: any) => ({
                          label: option?.name,
                          value: option?.id,
                        }))}
                        required
                        fullWidth
                      />
                    </Grid>

                    {/* Amount Field (optional for single-use) */}
                    {isSingleUse && (
                      <Grid item xs={12}>
                        <Controls.Input
                          label="Betrag"
                          name="amount"
                          value={formValues.amount ? formValues.amount : ""}
                          onChange={handleChange}
                          fullWidth
                          type="number"
                        />
                      </Grid>
                    )}

                    {/* First Name, Last Name */}
                    <Grid item xs={12} md={6}>
                      <Controls.Input
                        name="firstName"
                        label="Vorname"
                        value={formValues.firstName}
                        onChange={handleChange}
                        required
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controls.Input
                        name="lastName"
                        label="Nachname"
                        value={formValues.lastName}
                        onChange={handleChange}
                        required
                        fullWidth
                      />
                    </Grid>

                    {/* IBAN, BIC */}
                    <Grid item xs={12} md={6}>
                      <Controls.Input
                        label="IBAN"
                        name="iban"
                        value={formValues.iban}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Controls.Input
                        label="BIC"
                        name="bic"
                        value={formValues.bic}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right Side: Unique Pair Selector */}
                <Grid item xs={12} md={5} sx={{ marginTop: "44px" }}>
                  <Card elevation={3} style={{ padding: "10px" }}>
                    <CardContent>
                      <Controls.Select
                        name="uniquePair"
                        label="Existierende Bankverbindungen"
                        value=""
                        onChange={handlePairSelect}
                        options={uniquePairsData?.map((pair: any) => ({
                          label: `${pair.firstName} ${pair.lastName} - IBAN: ${pair.iban}`,
                          value: `${pair.firstName} ${pair.lastName} - IBAN: ${pair.iban}`,
                        }))}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleTransactionClose}
                variant="text"
                disabled={loading}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={!isFormValid || loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Speichern"
                )}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  },
);

export default CreditPermission;
